
/**
 * Utility to handle printing of the fighter jet designs.
 * Supports both standard iframe printing and iOS window.open printing.
 * Handles SVG ID duplication issues for multiple copies.
 */

import { fixIosSvgFilters } from "@/utils/svgUtils";

export const PrintManager = {
    /**
     * Main entry point for printing.
     * @param {HTMLElement} printableElement - The DOM element containing the content to print.
     * @param {number} numCopies - Number of copies to print.
     */
    print(printableElement, numCopies = 1) {
        if (!printableElement) {
            console.error("Printable element not found");
            return;
        }

        const firstPage = printableElement.querySelector('.print-page');
        if (!firstPage) {
            console.error("No print-page found");
            return;
        }

        const pages = this.preparePages(firstPage, numCopies);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
            this.printViaWindow(pages);
        } else {
            this.printViaIframe(pages);
        }
    },

    /**
     * Clones the first page and creates multiple copies with unique IDs.
     * @param {HTMLElement} firstPage 
     * @param {number} numCopies 
     * @returns {HTMLElement[]} Array of page elements
     */
    preparePages(firstPage, numCopies) {
        // Clone and create all copies
        const pages = [firstPage.cloneNode(true)];

        for (let copyIndex = 2; copyIndex <= numCopies; copyIndex++) {
            const clonedPage = firstPage.cloneNode(true);
            clonedPage.setAttribute('data-copy-index', copyIndex);

            // Find all elements with IDs and update them
            const allElements = clonedPage.querySelectorAll('[id]');
            const idMap = new Map();

            // First pass: create new unique IDs
            allElements.forEach(el => {
                const oldId = el.getAttribute('id');
                const newId = `${oldId}_copy${copyIndex}`;
                idMap.set(oldId, newId);
                el.setAttribute('id', newId);
            });

            // Second pass: update all references
            const allClonedElements = clonedPage.querySelectorAll('*');
            allClonedElements.forEach(el => {
                ['fill', 'stroke'].forEach(attr => {
                    const value = el.getAttribute(attr);
                    if (value && value.startsWith('url(#')) {
                        const oldId = value.substring(5, value.length - 1);
                        if (idMap.has(oldId)) {
                            el.setAttribute(attr, `url(#${idMap.get(oldId)})`);
                        }
                    }
                });

                ['xlink:href', 'href'].forEach(attr => {
                    const value = el.getAttribute(attr);
                    if (value && value.startsWith('#')) {
                        const oldId = value.substring(1);
                        if (idMap.has(oldId)) {
                            el.setAttribute(attr, `#${idMap.get(oldId)}`);
                        }
                    }
                });
            });

            pages.push(clonedPage);
        }
        return pages;
    },

    /**
     * Generates the HTML string for the print document.
     * @param {HTMLElement[]} pages 
     * @returns {string} HTML content
     */
    generateHtml(pages) {
        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Print</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color-adjust: exact;
        }
        
        body, html {
            width: 100%;
            height: 100%;
            background: white;
            margin: 0;
            padding: 0;
        }
        
        .print-page {
            width: 100%;
            height: 100vh;
            page-break-inside: avoid;
            break-inside: avoid;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .print-page:not(:last-child) {
            page-break-after: always;
            break-after: page;
        }
        
        .print-page > * {
            display: block;
            margin: auto;
            max-width: 100%;
            max-height: 100%;
            width: auto !important;
            height: auto !important;
        }

        /* Hide content on screen to prevent flash of unstyled content on iOS */
        @media screen {
            .print-page {
                display: none;
            }
        }
        
        @media print {
            @page {
                size: auto;
            }
            
            body, html {
                margin: 0;
                padding: 0;
            }
            
            .print-page {
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .print-page > * {
                display: block;
                margin: auto;
                max-width: 100%;
                max-height: 100%;
                width: auto !important;
                height: auto !important;
            }
        }
    </style>
</head>
<body>
${pages.map(page => page.outerHTML).join('\n')}
<script>
    window.onload = function() {
        setTimeout(function() {
            window.focus();
            window.print();
            // Close window only if opened as popup (iOS)
            if (window.opener) {
                setTimeout(function() {
                    window.close();
                }, 100);
            }
        }, 500);
    };
</script>
</body>
</html>
`;
    },

    /**
     * Print using window.open (for iOS).
     * @param {HTMLElement[]} pages 
     */
    printViaWindow(pages) {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups for printing');
            return;
        }

        const htmlContent = this.generateHtml(pages);
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    },

    /**
     * Print using a hidden iframe (for Desktop/Android).
     * @param {HTMLElement[]} pages 
     */
    printViaIframe(pages) {
        // Remove existing print frame if any
        const existingFrame = document.getElementById('print-iframe');
        if (existingFrame) {
            document.body.removeChild(existingFrame);
        }

        // Inject print-only styles to hide everything except the iframe
        const styleId = 'print-override-style';
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) document.head.removeChild(existingStyle);

        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            @media print {
                body > *:not(#print-iframe) {
                    display: none !important;
                }
                #print-iframe {
                    display: block !important;
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                    z-index: 2147483647 !important;
                    opacity: 1 !important;
                }
            }
        `;
        document.head.appendChild(style);

        const iframe = document.createElement('iframe');
        iframe.id = 'print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.style.zIndex = '-9999';
        // iframe.style.opacity = '0'; // Keep visible for print detection
        iframe.style.pointerEvents = 'none';
        document.body.appendChild(iframe);

        const htmlContent = this.generateHtml(pages);

        // Write content to iframe
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Cleanup logic
        const cleanup = () => {
            if (document.body.contains(iframe)) {
                // document.body.removeChild(iframe); // Debug: keep iframe for inspection
            }
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };

        // Attach listener for cleanup
        // We try to attach to the iframe window's afterprint event
        const iframeWindow = iframe.contentWindow;
        try {
            iframeWindow.addEventListener('afterprint', cleanup);
        } catch (e) {
            console.warn('Failed to add afterprint listener', e);
        }

    },

    /**
     * Helper to inline external resources (images/use tags) in SVG to Data URIs
     * to ensure they render correctly in html2canvas/jspdf.
     */
    async inlineSvgResources(element) {
        const uses = element.querySelectorAll('use');
        const images = element.querySelectorAll('image');

        const toBase64 = async (url) => {
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.warn('Failed to inline resource:', url, e);
                return null;
            }
        };

        // Process <use> tags
        for (const use of uses) {
            let href = use.getAttribute('xlink:href') || use.getAttribute('href');
            if (href && !href.startsWith('#') && !href.startsWith('data:')) {
                // It's an external URL
                const dataUri = await toBase64(href);
                if (dataUri) {
                    const img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
                    img.setAttribute('href', dataUri);
                    // Copy dimensions/transforms
                    ['x', 'y', 'width', 'height', 'transform', 'class', 'style'].forEach(attr => {
                        if (use.hasAttribute(attr)) img.setAttribute(attr, use.getAttribute(attr));
                    });
                    use.parentNode.replaceChild(img, use);
                }
            }
        }

        // Process <image> tags
        for (const img of images) {
            let href = img.getAttribute('xlink:href') || img.getAttribute('href');
            if (href && !href.startsWith('data:')) {
                const dataUri = await toBase64(href);
                if (dataUri) {
                    img.setAttribute('href', dataUri);
                }
            }
        }
    },

    /**
     * Traverses SVG elements and explicitly applies computed styles (fill, stroke) 
     * to leaf nodes to work around html2canvas inheritance issues.
     */
    normalizeSvgStyles(element) {
        const svgElements = element.querySelectorAll('svg *');
        svgElements.forEach(el => {
            const style = window.getComputedStyle(el);
            // Apply fill if it's not set explicitly and is inherited
            if (style.fill && style.fill !== 'none' && !el.hasAttribute('fill') && !el.style.fill) {
                el.style.fill = style.fill;
            }
            // Apply stroke
            if (style.stroke && style.stroke !== 'none' && !el.hasAttribute('stroke') && !el.style.stroke) {
                el.style.stroke = style.stroke;
            }
            // Apply font properties for text
            if (el.tagName.toLowerCase() === 'text' || el.tagName.toLowerCase() === 'tspan') {
                if (!el.style.fontFamily) el.style.fontFamily = style.fontFamily;
                if (!el.style.fontSize) el.style.fontSize = style.fontSize;
                if (!el.style.fontWeight) el.style.fontWeight = style.fontWeight;
            }
        });
    },

    /**
     * Replaces internal <use> tags with their referenced content to fix 
     * html2canvas limitations with shadow DOM and style inheritance.
     */
    flattenSvgUses(element) {
        const uses = Array.from(element.querySelectorAll('use'));
        uses.forEach(use => {
            const href = use.getAttribute('xlink:href') || use.getAttribute('href');
            if (href && href.startsWith('#')) {
                const id = href.substring(1);
                // Search within the element first (it's a clone), or fallback to document
                const symbol = element.querySelector(`[id="${id}"]`) || document.getElementById(id);

                if (symbol) {
                    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');

                    // Copy all attributes from <use> to <g>
                    Array.from(use.attributes).forEach(attr => {
                        // Skip hrefs
                        if (attr.name !== 'href' && attr.name !== 'xlink:href') {
                            g.setAttribute(attr.name, attr.value);
                        }
                    });

                    // Handle x/y as absolute translation if present
                    const x = parseFloat(use.getAttribute('x') || 0);
                    const y = parseFloat(use.getAttribute('y') || 0);
                    if (x !== 0 || y !== 0) {
                        const existingTransform = g.getAttribute('transform') || '';
                        g.setAttribute('transform', `${existingTransform} translate(${x}, ${y})`.trim());
                        g.removeAttribute('x');
                        g.removeAttribute('y');
                    }

                    // Clone symbol's children
                    Array.from(symbol.children).forEach(child => {
                        g.appendChild(child.cloneNode(true));
                    });

                    use.parentNode.replaceChild(g, use);
                }
            }
        });
    },

    /**
     * Downloads the content as a PDF.
     * @param {HTMLElement} element - The element to print.
     * @param {string} filename - The name of the file to save.
     */
    async downloadPdf(element, filename = 'download.pdf', password = null) {
        if (!element) return;

        // Dynamic import
        const { default: jsPDF } = await import('jspdf');
        const { default: html2canvas } = await import('html2canvas');

        // 1. Manually clone the element to process it without affecting the UI or relying on sync onclone
        const clone = element.cloneNode(true);

        // 2. Setup the clone for capture (reset hidden styles)
        // Use off-screen positioning instead of z-index to enforce rendering on iOS
        clone.style.position = 'fixed';
        clone.style.left = '-10000px';
        clone.style.top = '0';
        clone.style.zIndex = '1'; // Positive z-index to ensure it's "visible" to the engine
        clone.style.opacity = '1';
        clone.style.backgroundColor = '#ffffff';
        clone.style.width = element.offsetWidth + 'px';
        clone.style.height = element.offsetHeight + 'px';

        document.body.appendChild(clone);

        try {
            // 3. Inline resources asynchronously
            await this.inlineSvgResources(clone);

            // 3a. Flatten internal <use> tags to ensure inheritance works for html2canvas
            this.flattenSvgUses(clone);

            // 3b. Normalize SVG styles to fix inheritance issues (e.g. black numbers)
            this.normalizeSvgStyles(clone);

            // 3c. Fix filters on iOS (using shared utility)
            fixIosSvgFilters(clone);

            // Small delay to ensure layout is calculated by browser
            await new Promise(resolve => setTimeout(resolve, 100));

            // 4. Capture
            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            // 5. Generate PDF
            const imgData = canvas.toDataURL('image/png');

            const pdfOptions = {
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            };

            if (password) {
                pdfOptions.encryption = {
                    userPassword: password,
                    ownerPassword: password,
                    userPermissions: ['print'],
                    encryptionAlgorithm: 'AES',
                    keyLength: 128
                };
            }

            const pdf = new jsPDF(pdfOptions);

            const imgProps = pdf.getImageProperties(imgData);

            // A4 Dimensions: 210mm x 297mm
            const pdfPageWidth = pdf.internal.pageSize.getWidth();
            const pdfPageHeight = pdf.internal.pageSize.getHeight();

            const margin = 10;
            const contentWidth = pdfPageWidth - (margin * 2);
            const contentHeight = pdfPageHeight - (margin * 2);

            const imgRatio = imgProps.width / imgProps.height;
            let finalWidth = contentWidth;
            let finalHeight = contentWidth / imgRatio;

            if (finalHeight > contentHeight) {
                finalHeight = contentHeight;
                finalWidth = contentHeight * imgRatio;
            }

            const x = margin + (contentWidth - finalWidth) / 2;
            const y = margin + (contentHeight - finalHeight) / 2;

            pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
            pdf.save(filename);

        } catch (error) {
            console.error("PDF Generation Failed:", error);
            throw error;
        } finally {
            // 6. Cleanup
            if (document.body.contains(clone)) {
                document.body.removeChild(clone);
            }
        }
    }
};
